<template>
    <div :class="namespaceClass()" v-if="$store.state.benchmark.includeBenchmark">
        <div :class="namespaceClass('-benchmark-detail')">
            <div :class="namespaceClass('-select-benchmark')">
                <mds-section :title="`1. ${this.getUILabel('benchmark_select_title')}`" :size="8" border="tertiary" bold>
                    <template v-slot:default>
                        <mds-form size="small">
                            <mds-fieldset hidden-legend variation="radio-group" size="small">
                                <mds-radio-button
                                    v-for="(benchmark, index) in benchmarkTypes"
                                    :e2e="`benchmark-option-${index}`"
                                    :key="benchmark.value"
                                    :value="benchmark.value"
                                    name="selectBenchmark"
                                    size="small"
                                    :checked="selectedBenchmarkType === benchmark.value"
                                    :disabled="isIndex && holdingsLoading"
                                    @input="handleSelectBenchmark"
                                >
                                    {{ getUILabel(benchmark.labelId) }}
                                </mds-radio-button>
                            </mds-fieldset>
                        </mds-form>
                    </template>
                </mds-section>
                <div :class="namespaceClass('-select-benchmark__result')" v-if="isIndex || isCustomBenchmark">
                    <!-- Index Benchmark -->
                    <template v-if="isIndex">
                        <mds-search-field
                            e2e="benchmark-index-search"
                            :class="namespaceClass('__search-field')"
                            :placeholder="`${getUILabel('common_search')} ...`"
                            :value="indexSearchWord"
                            variation="secondary"
                            size="small"
                            @input="searchIndexes"
                        >
                        </mds-search-field>
                        <div :class="namespaceClass('__index-list')">
                            <mds-section v-if="mstarMarketIndexList.length > 0" :size="9" title="MORNINGSTAR MARKET INDEXES" border="none" bold>
                                <mds-list-group size="small" variation="selection">
                                    <mds-list-group-item
                                        v-for="(item, index) in mstarMarketIndexList"
                                        :e2e="`morningstar-index-benchmark-${index}`"
                                        :key="`${item.identifier}-${index}`"
                                        :text="item.securityName"
                                        :active="selectedIndexId === item.securityId"
                                        @mds-list-item-clicked="e => select(item)"
                                    >
                                        <template v-slot:mds-list-item-right>
                                            <template v-if="!!item.description">
                                                <mds-button
                                                    :class="namespaceClass('__disclosure-icon')"
                                                    variation="icon-only"
                                                    size="small"
                                                    icon="info-circle"
                                                    @mouseover="e => showDisclosure(item)"
                                                    @mouseleave="e => hideDisclosure(item)"
                                                    :id="`${item.identifier}-${index}`"
                                                >
                                                    {{ getUILabel("benchmark_disclosure") }}
                                                </mds-button>
                                                <mds-tooltip
                                                    :triggered-by="`${item.identifier}-${index}`"
                                                    v-model="item.showDisclosure"
                                                    :position="['bottom-left']"
                                                >
                                                    {{ item.description }}
                                                </mds-tooltip>
                                            </template>
                                        </template>
                                    </mds-list-group-item>
                                </mds-list-group>
                            </mds-section>
                            <mds-section v-if="marketIndexList.length > 0" :size="9" title="MARKET INDEXES" border="none" bold>
                                <mds-list-group size="small" variation="selection">
                                    <mds-list-group-item
                                        v-for="(item, index) in marketIndexList"
                                        :e2e="`market-index-benchmark-${index}`"
                                        :key="`${item.identifier}-${index}`"
                                        :text="item.securityName"
                                        :active="selectedIndexId === item.securityId"
                                        @mds-list-item-clicked="e => select(item)"
                                    >
                                        <template v-slot:mds-list-item-right>
                                            <template v-if="!!item.description">
                                                <mds-button
                                                    :class="namespaceClass('__disclosure-icon')"
                                                    variation="icon-only"
                                                    size="small"
                                                    icon="info-circle"
                                                    @mouseover="e => showDisclosure(item)"
                                                    @mouseleave="e => hideDisclosure(item)"
                                                    :id="`${item.identifier}-${index}`"
                                                >
                                                    {{ getUILabel("benchmark_disclosure") }}
                                                </mds-button>
                                                <mds-tooltip
                                                    :triggered-by="`${item.identifier}-${index}`"
                                                    v-model="item.showDisclosure"
                                                    :position="['bottom-left']"
                                                >
                                                    {{ item.description }}
                                                </mds-tooltip>
                                            </template>
                                        </template>
                                    </mds-list-group-item>
                                </mds-list-group>
                            </mds-section>
                            <mds-list-group v-if="indexSearchWord !== ''" size="small" variation="selection">
                                <mds-list-group-item
                                    v-for="(item, index) in searchedIndexes"
                                    :e2e="`index-benchmark-search-${index}`"
                                    :key="`${item.identifier}-${index}`"
                                    :text="item.securityName"
                                    :active="selectedIndexId === item.securityId"
                                    @mds-list-item-clicked="e => select(item)"
                                >
                                </mds-list-group-item>
                            </mds-list-group>
                        </div>
                    </template>
                    <template v-else-if="isCustomBenchmark">
                        <!-- Custom Benchmark -->
                        <mds-search-field
                            :class="namespaceClass('__search-field')"
                            e2e="custom-benchmark-search"
                            :placeholder="`${getUILabel('common_search')} ...`"
                            :value="customBmkKey"
                            variation="secondary"
                            size="small"
                            @input="searchCustomBmks"
                        >
                        </mds-search-field>
                        <div :class="namespaceClass('__custom-benchmark-list')">
                            <mds-section :title="getUILabel('BENCHMARK_SAVED_CUSTOM')" :size="9" border="none" bold>
                                <mds-list-group variation="selection" size="small">
                                    <mds-list-group-item
                                        v-for="(item, index) in customBenchmarks"
                                        :e2e="`custom-benchmark-${index}`"
                                        :key="`${item.value}-${index}`"
                                        :text="item.label"
                                        :active="customBenchmarkId === item.value"
                                        @mds-list-item-clicked="e => select(item)"
                                    >
                                    </mds-list-group-item>
                                </mds-list-group>
                            </mds-section>
                        </div>
                    </template>
                    <mds-loader v-show="benchmarkLoading"></mds-loader>
                </div>
            </div>
            <div :class="namespaceClass('-benchmark-holdings')">
                <mds-section :title="`2. ${this.getUILabel('benchmark_review_indexes')}`" border="tertiary" bold :size="8">
                    <template v-slot:default>
                        <mds-loader v-show="holdingsLoading"></mds-loader>
                        <div v-if="!holdingsLoading && benchmarkHoldings.length === 0" style="display:flex;align-items:center;justify-content:center">
                            <mds-empty-state class="custom-empty-state-class" :message="getUILabel('benchmark_no_selection_added')" size="small" title="">
                            </mds-empty-state>
                        </div>
                        <mds-data-table
                            v-else-if="!holdingsLoading"
                            e2e="benchmark-holdings"
                            :header-configs="columnFields"
                            :row-data="benchmarkHoldingsData"
                            size="small"
                        >
                            <template v-slot:body-cell="cellData">
                                <template v-if="cellData.headerConfig.fieldName === 'name'">
                                    <mds-icon
                                        e2e="invalid-benchmark"
                                        v-if="isInvalidHolding(cellData.rowData)"
                                        name="alert-fill"
                                        size="small"
                                        class="alert-icon"
                                    ></mds-icon>
                                    {{ cellData.rowData.name }}
                                </template>
                            </template>
                        </mds-data-table>
                    </template>
                </mds-section>
            </div>
        </div>
    </div>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import MdsRadioButton from "@mds/radio-button";
import { MdsButton } from "@mds/button";
import MdsForm from "@mds/form";
import MdsFieldset from "@mds/fieldset";
import MdsSection from "@mds/section";
import MdsSearchField from "@mds/search-field";
import MdsEmptyState from "@mds/empty-state";
import { MdsListGroup, MdsListGroupItem } from "@mds/list-group";
import { MdsDataTable } from "@mds/data-table";
import MdsTooltip from "@mds/tooltip";
import MdsLoader from "@mds/loader";
import { mapState } from "vuex";
import * as types from "../store/mutation-types.js";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import MdsIcon from "@mds/icon";
import { roundNumber } from "@/util.js";
const REQUEST_ABORTED = "request aborted";

export default {
    name: "benchmark",
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    components: {
        MdsLoader,
        MdsRadioButton,
        MdsButton,
        MdsForm,
        MdsSection,
        MdsFieldset,
        MdsSearchField,
        MdsListGroup,
        MdsListGroupItem,
        MdsTooltip,
        MdsEmptyState,
        MdsDataTable,
        MdsIcon
    },
    data() {
        return {
            defaultConfig: {
                labels: this.$store.state.labels.labels,
                format: {
                    "dd-mm-yyyy": {
                        dataType: "date",
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit"
                    },
                    number: {
                        dataType: "number",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }
                }
            },
            benchmarkLoading: false,
            holdingsLoading: false,
            selectedBenchmarkType: "", //currently selected benchmarkType
            searchedIndexes: [],
            mstarMarketIndexList: [],
            marketIndexList: [],
            customBmkKey: "",
            customBenchmarks: [],
            indexSearchWord: "",
            benchmarkHoldings: [],
            customBenchmarkId: "",
            customBenchmarkName: "",
            selectedIndexId: "",
            columnFields: [],
            rowsData: []
        };
        // BenchmarkType
        // 0: By Index
        // 1: Auto Benchmark by Asset Allocation,
        // 2: Auto Benchmark by Morningstar Category
        // 3: Custom Benchmark
    },
    computed: {
        ...mapState("benchmark", ["includeBenchmark"]),
        benchmarkHoldingsData() {
            const totalRow = [
                {
                    name: this.getUILabel("common_total"),
                    identifier: "",
                    weight: this.total,
                    class: "total-row",
                    id: "total"
                }
            ];
            return totalRow.concat(this.benchmarkHoldings);
        },
        relatedPortfolioId() {
            return this.$store.state.settings.rawPortfolio.id;
        },
        benchmarkTypes() {
            const benchmarks = this.$store.getters["benchmark/benchmarkTypes"].map(item => {
                return {
                    labelId: item.labelId,
                    value: item.id
                };
            });
            const isCustomActive = this.$store.getters["settings/hasCustomBenchmark"];
            return isCustomActive ? benchmarks : benchmarks.filter(item => item.labelId !== "benchmark_custom");
        },
        portfolioEndDate() {
            let endDate = "";
            this.$store.state.holdings.securities.forEach(item => {
                if (endDate === "" || new Date(endDate) < new Date(item.endDate)) {
                    endDate = item.endDate;
                }
            });
            return endDate;
        },
        total() {
            let total = 0;
            this.benchmarkHoldings.forEach(h => {
                total += h.weight;
            });
            return this.formatter.number(total);
        },
        languageId() {
            return this.$store.state.settings.preferences.languageId;
        },
        isIndex() {
            return this.selectedBenchmarkType === 0;
        },
        isCustomBenchmark() {
            return this.selectedBenchmarkType === 3;
        }
    },
    created() {
        this.selectedBenchmarkType = this.$store.state.benchmark.benchmarkType;
        this.columnFields = [
            {
                fieldName: "name",
                text: this.getUILabel("benchmark_security_name"),
                width: "60%"
            },
            {
                fieldName: "identifier",
                text: this.getUILabel("benchmark_security_identifier"),
                width: "20%"
            },
            {
                fieldName: "weight",
                text: this.getUILabel("benchmark_weight_with_percent"),
                transform: this.formatWeight,
                width: "20%",
                align: "right"
            }
        ];
    },
    watch: {
        includeBenchmark() {
            this.verifyBenchmarkError();
        },
        benchmarkHoldings() {
            this.verifyBenchmarkError();
        }
    },
    methods: {
        verifyBenchmarkError() {
            if (this.includeBenchmark && this.benchmarkHoldings.length > 0) {
                this.verifyBenchmarkHoldings(this.benchmarkHoldings);
            } else {
                this.$emit("valid-benchmark-error", {
                    key: "benchmark",
                    payload: null
                });
            }
        },
        initBenchmarkHoldings() {
            if (this.$store.state.benchmark.includeBenchmark) {
                const { benchmarkType, benchmarkHoldings, customBenchmarkId } = this.$store.state.benchmark;
                this.customBenchmarkId = customBenchmarkId;
                this.selectedBenchmarkType = benchmarkType;
                this.loadBenchmarks(benchmarkType);
                if (this.benchmarkHoldings.length === 0) {
                    this.loadBenchmarkHoldings({
                        benchmarkType,
                        benchmarkHoldings,
                        customBenchmarkId,
                        checkIndexPrice: true
                    });
                }
            }
        },
        changeIncludeBenchmark(checked) {
            if (checked) {
                //only first check on, use preference benchmark setting.
                const isFirstCheckOn = this.$store.state.benchmark.benchmarkType === null;
                if (isFirstCheckOn) {
                    this.$store.commit(`benchmark/${types.UPDATE_BENCHMARK_SETTING}`, this.$store.getters["benchmark/preferenceBenchmark"]);
                }
                this.initBenchmarkHoldings();
            }
            this.$store.commit(`benchmark/${types.SET_INCLUDE_BENCHMARK}`, checked);
        },
        handleSelectBenchmark(selectedBenchmarkType) {
            if (this.selectedBenchmarkType === selectedBenchmarkType) {
                return;
            }
            this.$abortActiveRequests();
            this.benchmarkLoading = false;
            this.holdingsLoading = false;
            this.benchmarkHoldings = [];
            this.selectedIndexId = "";
            this.customBenchmarkId = "";
            this.selectedBenchmarkType = selectedBenchmarkType;
            this.loadBenchmarks(selectedBenchmarkType);
            const { customBenchmarkId, benchmarkHoldings } = this.$store.state.benchmark;
            this.loadBenchmarkHoldings({
                benchmarkType: selectedBenchmarkType,
                benchmarkHoldings,
                customBenchmarkId,
                checkIndexPrice: true
            });
            if (selectedBenchmarkType === 3) {
                this.customBmkKey = "";
            } else if (selectedBenchmarkType === 0) {
                this.indexSearchWord = "";
            }
        },
        async loadBenchmarks(benchmarkType) {
            const isIndex = benchmarkType === 0;
            const isCustomBenchmark = benchmarkType === 3;
            if (isIndex) {
                // Index
                await this.loadDefaultIndex();
            } else if (isCustomBenchmark) {
                //Custom benchmark
                await this.loadDefaultCustomBenchmarkList();
            }
        },
        async loadBenchmarkHoldings({ benchmarkType, benchmarkHoldings, customBenchmarkId, checkIndexPrice = false }) {
            const isAutoBenchmark = benchmarkType === 1 || benchmarkType === 2;
            const isCustomBenchmark = benchmarkType === 3;
            const isIndex = benchmarkType === 0;
            let holdings = [];
            this.holdingsLoading = true;
            try {
                if (isAutoBenchmark) {
                    holdings = await this.loadAutoBenchmarkHoldings(benchmarkType);
                } else if (isCustomBenchmark && customBenchmarkId) {
                    holdings = await this.getHoldingsByCustomBenchmarkId(customBenchmarkId);
                } else if (isIndex && benchmarkHoldings && benchmarkHoldings.length > 0) {
                    holdings = await this.loadIndexHoldings(benchmarkHoldings, checkIndexPrice);
                }
                this.benchmarkHoldings = holdings;
            } catch (err) {
                if (err.message !== REQUEST_ABORTED) {
                    throw err;
                }
            }
            this.holdingsLoading = false;
            return holdings;
        },
        async loadIndexHoldings(benchmarkHoldings, checkIndexPrice) {
            if (checkIndexPrice) {
                const searchedResult = await this.getIndexes({
                    universeType: "XI",
                    searchValue: benchmarkHoldings[0].securityId,
                    languageId: this.languageId,
                    portfolioEndDate: this.portfolioEndDate
                });
                if (searchedResult.securities.length > 0) {
                    benchmarkHoldings = searchedResult.securities.map(item => ({
                        ...item,
                        securityName: item.name
                    }));
                }
            }
            return benchmarkHoldings.map(item => {
                const len = benchmarkHoldings.length;
                const weight = roundNumber(1 / len) * 100;
                return {
                    id: item.securityId,
                    name: item.securityName,
                    identifier: item.identifier,
                    weight: weight,
                    havePrice: item.havePrice || false
                };
            });
        },
        async loadAutoBenchmarkHoldings(benchmarkType) {
            const portfolio = this.$store.state.settings.rawPortfolio;
            const assetGroups = this.$store.getters["settings/broadAssetAllocationGroup"];
            return this.getAutoBenchmarkHoldings(benchmarkType, portfolio, assetGroups);
        },
        async loadDefaultIndex() {
            this.benchmarkLoading = true;
            this.searchedIndexes = [];
            try {
                const data = await this.getStandardBenchmark({
                    languageId: this.languageId,
                    enddate: this.portfolioEndDate
                });
                this.mstarMarketIndexList = data.mstarMarketIndexes;
                this.marketIndexList = data.marketIndexes;
            } catch (err) {
                if (err.message !== REQUEST_ABORTED) {
                    throw err;
                }
            }
            this.benchmarkLoading = false;
        },
        async loadDefaultCustomBenchmarkList() {
            this.benchmarkLoading = true;
            this.customBenchmarks = [];
            try {
                this.customBenchmarks = await this.loadCustomBenchmarkList();
            } catch (err) {
                if (err.message !== REQUEST_ABORTED) {
                    throw err;
                }
            }
            this.benchmarkLoading = false;
        },
        loadCustomBenchmarkList(params) {
            return this.getCustomBenchmarks(params).then(data => {
                return data.entities.map(item => {
                    return {
                        value: item.id,
                        label: item.name
                    };
                });
            });
        },
        async getHoldingsByCustomBenchmarkId(customBenchmarkId) {
            const securities = await this.getCustomBenchmarkHoldings(customBenchmarkId, {
                enddate: this.portfolioEndDate
            });
            return this.mapSecurities(securities);
        },
        getAutoBenchmarkHoldings(benchmarkType, portfolio, assetGroups) {
            return this.$fetch({
                url: `/api/v1/benchmark/autobenchmark/${benchmarkType}`,
                method: "POST",
                body: JSON.stringify({
                    portfolio: portfolio,
                    assetGroups: assetGroups
                })
            }).then(securities => {
                return this.mapSecurities(securities);
            });
        },
        mapSecurities(securities) {
            return securities.map(security => ({
                id: security.securityId,
                name: security.securityName,
                identifier: security.identifier,
                weight: security.weight,
                havePrice: security.havePrice
            }));
        },
        getStandardBenchmark(params) {
            const searchParams = new URLSearchParams(params);
            return this.$fetch({
                url: `/api/v1/benchmark/standardbenchmark?${searchParams.toString()}`,
                method: "GET"
            });
        },
        getCustomBenchmarks(params) {
            const searchParams = new URLSearchParams(params);
            const searchString = searchParams.toString() ? `?${searchParams.toString()}` : "";
            return this.$fetch({
                url: `/api/v1/benchmark/custombenchmark${searchString}`,
                method: "GET"
            });
        },
        getCustomBenchmarkHoldings(benchmarkId, params) {
            const searchParams = new URLSearchParams(params);
            return this.$fetch({
                url: `/api/v1/benchmark/custombenchmark/${benchmarkId}?${searchParams.toString()}`,
                method: "GET"
            });
        },
        getIndexes(data) {
            return this.$fetch({
                url: `/api/v1/universesearch`,
                method: "POST",
                body: JSON.stringify(data)
            });
        },
        isValidHoldings(holdings) {
            return holdings.every(item => item.havePrice === true);
        },
        isInvalidHolding(holding) {
            return this.benchmarkHoldings.some(item => item.id === holding.id) && !holding?.havePrice;
        },
        verifyBenchmarkHoldings(holdings) {
            const error = {
                key: "benchmark",
                payload: null
            };
            if (!this.isValidHoldings(holdings)) {
                error.payload = {
                    title: this.getUILabel("alert_benchmark_noprice_title"),
                    text: this.getUILabel("alert_benchmark_noprice_msg")
                };
            } else {
                this.saveBenchmarkSettings();
            }
            this.$emit("valid-benchmark-error", error);
        },
        saveBenchmarkSettings() {
            if (this.includeBenchmark && this.benchmarkHoldings.length > 0) {
                this.$store.commit(`benchmark/${types.SET_BENCHMARK_TYPE}`, this.selectedBenchmarkType);
                if (this.isIndex) {
                    this.$store.commit(
                        `benchmark/${types.SET_BENCHMARK_HOLDINGS}`,
                        this.benchmarkHoldings.map(item => {
                            return {
                                securityId: item.id,
                                securityName: item.name,
                                identifier: item.identifier,
                                havePrice: item.havePrice
                            };
                        })
                    );
                } else if (this.isCustomBenchmark && this.customBenchmarkId) {
                    this.$store.commit(`benchmark/${types.SET_CUSTOMBENCHMARK_ID}`, this.customBenchmarkId);
                    this.$store.commit(`benchmark/${types.SET_CUSTOMBENCHMARK_NAME}`, this.customBenchmarkName);
                }
            }
        },
        select(data) {
            let benchmarkType,
                customBenchmarkId,
                benchmarkHoldings = [];
            if (this.isIndex) {
                benchmarkType = 0;
                benchmarkHoldings = [
                    {
                        securityId: data.securityId,
                        securityName: data.securityName,
                        identifier: data.identifier,
                        weight: 100,
                        havePrice: data.havePrice
                    }
                ];
                this.selectedIndexId = data.securityId;
            } else if (this.isCustomBenchmark) {
                benchmarkType = 3;
                customBenchmarkId = data.value;
                this.customBenchmarkId = customBenchmarkId;
                this.customBenchmarkName = data.label;
            }
            this.loadBenchmarkHoldings({
                benchmarkType,
                benchmarkHoldings,
                customBenchmarkId
            });
        },
        searchCustomBmks(keyword) {
            this.customBmkKey = keyword.toLowerCase();
            this.customBenchmarks = [];
            clearTimeout(this.customBmkTimeout);
            this.customBmkTimeout = setTimeout(() => {
                this.$abortActiveRequests();
                this.benchmarkLoading = true;
                this.loadCustomBenchmarkList({
                    keyword: this.customBmkKey
                })
                    .then(data => {
                        this.benchmarkLoading = false;
                        this.customBenchmarks = data;
                    })
                    .catch(this.handleRequestAborted);
            }, 500);
        },
        searchIndexes(keyword) {
            this.mstarMarketIndexList = [];
            this.marketIndexList = [];
            this.indexSearchWord = keyword.toLowerCase();
            clearTimeout(this.indexTimeout);
            this.indexTimeout = setTimeout(() => {
                this.$abortActiveRequests();
                if (this.indexSearchWord !== "") {
                    this.benchmarkLoading = true;
                    this.getIndexes({
                        universeType: "XI",
                        searchValue: this.indexSearchWord,
                        languageId: this.languageId,
                        portfolioEndDate: this.portfolioEndDate
                    })
                        .then(data => {
                            this.benchmarkLoading = false;
                            this.searchedIndexes = data.securities.map(item => {
                                return {
                                    securityId: item.securityId,
                                    identifier: item.identifier,
                                    securityName: item.name,
                                    havePrice: item.havePrice
                                };
                            });
                        })
                        .catch(this.handleRequestAborted);
                } else {
                    this.loadDefaultIndex();
                }
            }, 500);
        },
        handleRequestAborted(err) {
            if (err.message !== REQUEST_ABORTED) {
                return Promise.reject(err);
            }
            return Promise.resolve();
        },
        formatWeight(weight) {
            return this.formatter.number(weight);
        },
        showDisclosure(item) {
            this.$set(item, "showDisclosure", true);
        },
        hideDisclosure(item) {
            this.$delete(item, "showDisclosure");
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~@mds/constants";
@import "~@mds/typography";
@import "@mds/icon/src/icon_mixins";
.benchmark {
    .mds-switch {
        margin-bottom: 10px;
    }

    &-benchmark-detail {
        display: flex;
    }

    &-select-benchmark {
        flex-basis: 50%;
        padding-right: 16px;

        /deep/ &__result {
            position: relative;
        }
    }

    &__index-list,
    &__custom-benchmark-list {
        max-height: 400px;
        overflow-y: auto;
        ::v-deep .mds-section___hypo {
            margin-bottom: $mds-space-2-x;
            padding: 0 3px 3px 3px;
        }
    }

    &__search-field {
        margin: $mds-space-2-x 0;
    }

    &__disclosure-icon {
        position: absolute;
        top: $mds-space-1-x;
        right: $mds-space-3-x;
    }

    &-benchmark-holdings {
        flex-basis: 50%;
        padding-left: 16px;
        position: relative;

        ::v-deep .mds-data-table__body___hypo .mds-data-table__row___hypo.total-row {
            background-color: $mds-background-color-light-gray;
            .mds-data-table__cell___hypo {
                font-weight: $mds-typography-font-weight-bold;
            }
        }
    }
}
.alert-icon {
    @include mds-icon-color($mds-text-color-error);
    position: relative;
    top: 2px;
}
</style>
